import type { ReactElement } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TodayIcon,
  FoodIcon,
  BodyIcon,
  MedsIcon,
  PlanIcon,
  TrainIcon,
  BrandMark,
  Cog,
} from '@/components/icons';
import { Avatar } from '@/components/domain/Avatar';
import { useAuth } from '@/auth/AuthProvider';
import { useActiveCycle } from '@/api/hooks/usePlan';

interface NavItem {
  to: string;
  labelKey: string;
  Icon: (props: { active?: boolean }) => ReactElement;
  match: 'exact' | 'prefix';
}

const ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.today', Icon: TodayIcon, match: 'exact' },
  { to: '/food', labelKey: 'nav.food', Icon: FoodIcon, match: 'prefix' },
  { to: '/body', labelKey: 'nav.body', Icon: BodyIcon, match: 'prefix' },
  { to: '/meds', labelKey: 'nav.meds', Icon: MedsIcon, match: 'prefix' },
  { to: '/train', labelKey: 'nav.train', Icon: TrainIcon, match: 'prefix' },
  { to: '/plan', labelKey: 'nav.plan', Icon: PlanIcon, match: 'prefix' },
];

function firstName(displayName: string | null | undefined): string {
  if (!displayName) return '';
  return displayName.split(/[\s@]/, 1)[0] ?? '';
}

export function Sidebar({ compact = false }: { compact?: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const cycle = useActiveCycle();

  const isActive = (item: NavItem) => {
    if (item.match === 'exact') return pathname === '/' || pathname === '';
    const path = pathname.replace(/\/+$/, '');
    return path === item.to || path.startsWith(`${item.to}/`);
  };

  const goalSnapshot = cycle.data;
  const weekProgress = goalSnapshot
    ? Math.min(1, goalSnapshot.weekCurrent / Math.max(1, goalSnapshot.cycle.weeks))
    : 0;

  const givenName = firstName(user?.name);

  return (
    <aside className={`sidebar${compact ? ' sidebar--compact' : ''}`}>
      <button
        type="button"
        className="sidebar__brand"
        onClick={() => navigate('/')}
        aria-label={t('components.sidebarHome')}
      >
        <span className="sidebar__brand-mark">
          <BrandMark size={16} />
        </span>
        {!compact && <span className="sidebar__brand-text">Optimize</span>}
      </button>

      <nav className="sidebar__nav">
        {ITEMS.map((item) => {
          const active = isActive(item);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.match === 'exact'}
              className={({ isActive: routerActive }) =>
                `sidebar__item${routerActive || active ? ' sidebar__item--active' : ''}`
              }
              aria-label={compact ? t(item.labelKey) : undefined}
              title={compact ? t(item.labelKey) : undefined}
            >
              <item.Icon active={active} />
              {!compact && <span>{t(item.labelKey)}</span>}
            </NavLink>
          );
        })}
      </nav>

      {!compact && goalSnapshot && (
        <button
          type="button"
          className="sidebar__goal"
          onClick={() => navigate('/plan')}
          aria-label={t('components.sidebarOpenPlan')}
        >
          <div className="sidebar__goal-label">{t('components.sidebarGoal')}</div>
          <div className="sidebar__goal-name">{goalSnapshot.cycle.name}</div>
          <div className="sidebar__goal-sub">
            {t('components.sidebarWeekOf', { current: goalSnapshot.weekCurrent, total: goalSnapshot.cycle.weeks })}
          </div>
          <div className="sidebar__goal-track">
            <div
              className="sidebar__goal-fill"
              style={{ width: `${Math.round(weekProgress * 100)}%` }}
            />
          </div>
        </button>
      )}

      <div className="sidebar__spacer" />

      {user?.isOwner && (
        <NavLink
          to="/admin"
          className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}
          aria-label={compact ? t('nav.admin') : undefined}
          title={compact ? t('nav.admin') : undefined}
        >
          <Cog />
          {!compact && <span>{t('nav.admin')}</span>}
        </NavLink>
      )}

      <button
        type="button"
        className="sidebar__user"
        onClick={() => navigate('/settings')}
        aria-label={t('components.sidebarAccountSettings')}
      >
        <Avatar name={user?.name ?? ''} size={32} />
        {!compact && (
          <div className="sidebar__user-text">
            <div className="sidebar__user-name">{givenName || user?.name || t('components.sidebarAccount')}</div>
            <div className="sidebar__user-sub">{t('components.sidebarPersonal')}</div>
          </div>
        )}
      </button>
    </aside>
  );
}
